/**
 * Serviços, clientes e a duração que liga os dois.
 *
 * `setClientDurationSchema` é o contrato mais importante do sistema: é onde o
 * barbeiro grava que o João de cabelo e barba leva 40 minutos.
 */

import { MAX_DURATION_MINUTES, MIN_DURATION_MINUTES } from '@barbearia/core';
import { z } from 'zod';
import {
  birthDateSchema,
  hexColorSchema,
  idSchema,
  phoneSchema,
  queryBooleanSchema,
} from './common.js';

/**
 * Instagram sem arroba e sem URL.
 *
 * O barbeiro digita "@joao", o cliente cola "instagram.com/joao", e os dois
 * querem dizer a mesma pessoa. Normalizar na entrada evita a mesma conta virar
 * três valores diferentes no banco — e a interface põe o arroba de volta.
 */
export const instagramHandleSchema = z
  .string()
  .trim()
  .transform((value) =>
    value
      .replace(/^https?:\/\/(www\.)?instagram\.com\//i, '')
      .replace(/^@/, '')
      .replace(/\/+$/, '')
      .toLowerCase(),
  )
  .refine(
    (value) => value === '' || /^[a-z0-9._]{1,30}$/.test(value),
    'Perfil do Instagram inválido',
  )
  .transform((value) => (value === '' ? undefined : value));

export const durationMinutesSchema = z
  .number()
  .int('Use minutos inteiros')
  .min(MIN_DURATION_MINUTES, `Mínimo de ${MIN_DURATION_MINUTES} minutos`)
  .max(MAX_DURATION_MINUTES, 'Duração longa demais para um atendimento');

export const priceCentsSchema = z
  .number()
  .int('Valor inválido')
  .min(0, 'O valor não pode ser negativo')
  .max(100_000_00, 'Valor acima do permitido');

// ── Serviços ────────────────────────────────────────────────────────────────

/**
 * Quantos serviços cabem num atendimento.
 *
 * Não é uma limitação técnica: é o que existe de verdade num atendimento de
 * barbearia. O número também segura a busca do "o que ainda cabe no dia", que é
 * exaustiva sobre os subconjuntos — seis serviços dão 63 combinações, todas
 * resolvidas em milissegundos.
 */
export const MAX_SERVICES_PER_APPOINTMENT = 6;

/**
 * Lista de serviços vinda da query string.
 *
 * Aceita `serviceIds=a,b` e `serviceIds=a&serviceIds=b`: a primeira forma é a
 * que o app monta, a segunda é a que sai naturalmente de quem testa a rota à
 * mão. Quem chega com o `serviceId` no singular — um app aberto antes da
 * publicação, ainda com a versão anterior em cache — é atendido em
 * `availabilityQuerySchema`, que aceita os dois nomes.
 */
export const serviceIdsQuerySchema = z
  .union([z.string(), z.array(z.string())])
  .transform((value) =>
    (Array.isArray(value) ? value : value.split(','))
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0),
  )
  .pipe(
    z
      .array(idSchema)
      .min(1, 'Escolha ao menos um serviço')
      .max(MAX_SERVICES_PER_APPOINTMENT, 'Serviços demais para um atendimento só'),
  );

/**
 * "Cabelo", "Cabelo e Barba", "Cabelo, Barba e Sobrancelha".
 *
 * Do jeito que o barbeiro fala, com a vírgula e o "e" no lugar — e não
 * "Cabelo, Barba, Sobrancelha", que é uma lista de sistema.
 */
export function describeServices(names: readonly string[]): string {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0] as string;
  return `${names.slice(0, -1).join(', ')} e ${names[names.length - 1]}`;
}

/**
 * "Cabelo +2" — o rótulo de quem tem 390px de tela.
 *
 * A linha do tempo e as listas mostram dezenas de atendimentos de uma vez: ali
 * o nome inteiro de três serviços não cabe, e cortado no meio ("Cabelo, Barb…")
 * não identifica nada. O primeiro nome mais a contagem cabem sempre.
 */
export function shortServicesLabel(names: readonly string[]): string {
  const [first, ...rest] = names;
  if (!first) return '';
  return rest.length === 0 ? first : `${first} +${rest.length}`;
}

/**
 * "55min", "1h", "1h10" — o tempo do jeito que se fala na barbearia.
 *
 * Mora aqui, e não na formatação do app, porque o servidor escreve as mesmas
 * frases: a mensagem de "não cabe" e o texto do WhatsApp precisam dizer o tempo
 * igual ao que está na tela ao lado.
 */
export function formatDurationLabel(minutes: number): string {
  if (minutes < 60) return `${minutes}min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours}h` : `${hours}h${String(rest).padStart(2, '0')}`;
}

export const createServiceSchema = z.object({
  name: z.string().trim().min(2, 'Informe o nome do serviço').max(80),
  description: z.string().trim().max(500).optional(),
  priceCents: priceCentsSchema.default(0),
  defaultDurationMinutes: durationMinutesSchema,
  color: hexColorSchema.optional(),
  sortOrder: z.number().int().min(0).max(999).optional(),
});

export const updateServiceSchema = createServiceSchema.partial().extend({
  active: z.boolean().optional(),
});

export interface ServiceView {
  id: string;
  name: string;
  description: string | null;
  priceCents: number;
  defaultDurationMinutes: number;
  color: string;
  active: boolean;
  sortOrder: number;
}

// ── Clientes ────────────────────────────────────────────────────────────────

/**
 * O cadastro de balcão continua sendo nome e telefone.
 *
 * Data de nascimento e Instagram entram como opcionais de verdade: o barbeiro
 * cadastra em pé, entre um corte e outro, e qualquer campo obrigatório a mais
 * transforma quinze segundos em desistência.
 */
export const createClientSchema = z.object({
  name: z.string().trim().min(2, 'Informe o nome').max(120),
  phone: phoneSchema,
  notes: z.string().trim().max(2000).optional(),
  birthDate: birthDateSchema.optional(),
  instagram: instagramHandleSchema.optional(),
});

export const updateClientSchema = z.object({
  name: z.string().trim().min(2, 'Informe o nome').max(120).optional(),
  phone: phoneSchema.optional(),
  /** `null` apaga a observação; ausente não mexe. */
  notes: z.string().trim().max(2000).nullable().optional(),
  approved: z.boolean().optional(),
  /** `null` apaga a data; ausente não mexe. */
  birthDate: birthDateSchema.nullable().optional(),
  instagram: instagramHandleSchema.nullable().optional(),
});

export const listClientsSchema = z.object({
  search: z.string().trim().max(120).optional(),
  includeArchived: queryBooleanSchema.default(false),
  /**
   * Trazer também os contatos do WhatsApp que ainda não são ficha.
   *
   * Desligado por padrão, e é a escolha certa: quem pede a lista de clientes
   * quer clientes. Só a busca da folha de agendar liga isto — é lá que o
   * barbeiro está procurando *uma pessoa*, e não importa a ele se ela já tem
   * cadastro. A aba Clientes continua sendo só de quem é cliente.
   */
  includeDrafts: queryBooleanSchema.default(false),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

export interface ClientDurationView {
  serviceId: string;
  serviceName: string;
  minutes: number;
  source: 'MANUAL' | 'SUGGESTION_ACCEPTED';
  /** Duração do serviço, para a tela mostrar o que está sendo personalizado. */
  serviceDefaultMinutes: number;
}

export interface ClientView {
  id: string;
  name: string;
  phone: string;
  notes: string | null;
  approved: boolean;
  hasAccount: boolean;
  totalAppointments: number;
  totalNoShows: number;
  lastVisitAt: string | null;
  archivedAt: string | null;
  durations: ClientDurationView[];
  /** 'AAAA-MM-DD'. Data de calendário — aniversário não tem fuso. */
  birthDate: string | null;
  /** Sem arroba; a interface põe. */
  instagram: string | null;
  photoUrl: string | null;
}

/** Um aniversariante do mês, já com o que a tela precisa mostrar. */
export interface BirthdayClientView {
  id: string;
  name: string;
  phone: string;
  phoneFormatted: string;
  /** Dia deste mês em que cai. */
  date: string;
  /** Assinado: 0 é hoje, negativo já passou neste mês. */
  daysUntil: number;
  turning: number;
  photoUrl: string | null;
}

// ── Durações personalizadas ─────────────────────────────────────────────────

/** O coração do produto. */
export const setClientDurationSchema = z.object({
  serviceId: idSchema,
  minutes: durationMinutesSchema,
  /** Marcar quando o barbeiro aceitou a sugestão do histórico. */
  fromSuggestion: z.boolean().default(false),
});

export interface DurationSuggestionView {
  serviceId: string;
  serviceName: string;
  currentMinutes: number;
  suggestedMinutes: number;
  observedAverageMinutes: number;
  sampleCount: number;
}
